import React, { useEffect, useRef, useState } from "react";
import FrontendRoutes from "./frontendRoutes";
import AdminRoutes from "./adminRoutes";
import ClientRoutes from "./clientRoutes";
import { connect, useDispatch } from "react-redux";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { loginSuccess, setCurrentPosition } from "../actions/allActions";
import InstructorRoutes from "./instructorRoutes";
import UserRoutes from "./userRoutes";
import RoleSwitchHandler from "../utility/RoleSwitchHandler";
import { normalizeLoginResponse } from "../utility/normalizeLoginResponse";
import i18n from "../i18n";
import { clearPersistedSession } from "../utility/clearPersistedSession";

const FRONTEND_PATHS = [
    "/signup",
    "/",
    "/aboutUs",
    "/contactUs",
    "/admin/forgotpassword",
    "/privacyPolicy",
    "/termsAndConditions",
    "/subscriptions",
    "/ourTeam",
    "/memorials",
    "/memorials/battalion-28",
    "/newsDetails",
    "/latestnews",
    "/login",
    "/forgotPassword",
    "/reset-password",
    "/admin/login",
    "/admin/reset-password",
];

const PUBLIC_AUTH_PATHS = [
    "/login",
    "/forgotPassword",
    "/reset-password",
    "/admin/login",
    "/admin/forgotPassword",
    "/admin/forgotpassword",
    "/admin/reset-password",
];

const resolvePosition = (role, currentPosition) => {
    if (currentPosition) {
        return currentPosition;
    }
    if (role === "client" || role === "instructor" || role === "user") {
        return role;
    }
    return null;
};

const isBase64Payload = (str) => {
    if (!str || str.length % 4 !== 0) return false;
    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(str)) return false;
    try {
        const decoded = atob(str);
        for (let i = 0; i < decoded.length; i++) {
            if (decoded.charCodeAt(i) < 32 || decoded.charCodeAt(i) > 126) {
                return false;
            }
        }
        return true;
    } catch {
        return false;
    }
};

const normalizeBase64Url = (str) => {
    if (!str) {
        return null;
    }
    let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    if (pad) {
        base64 += "=".repeat(4 - pad);
    }
    return base64;
};

const parseSessionToken = (str) => {
    if (!str) {
        return null;
    }

    const candidates = [str, normalizeBase64Url(str)].filter(Boolean);
    for (const candidate of candidates) {
        if (!isBase64Payload(candidate)) {
            continue;
        }
        try {
            return normalizeLoginResponse(JSON.parse(atob(candidate)));
        } catch {
            // try next candidate
        }
    }

    return null;
};

const extractSessionTokenFromPath = (pathname) => {
    const segments = pathname.split("/").filter(Boolean);
    if (!segments.length) {
        return null;
    }

    const lastSegment = segments[segments.length - 1];
    if (parseSessionToken(lastSegment)) {
        return lastSegment;
    }

    const joinedSegments = segments.join("");
    if (parseSessionToken(joinedSegments)) {
        return joinedSegments;
    }

    return null;
};

const getDashboardPathForRole = (role) => {
    if (role === "admin") {
        return "/admin/dashboard";
    }
    if (role === "client") {
        return "/dashboard";
    }
    if (role === "instructor") {
        return "/instructor/dashboard";
    }
    if (role === "user") {
        return "/user/dashboard";
    }
    return "/dashboard";
};

const isLogoutCallback = (location) =>
    location.search.includes("adminlogout=true") ||
    location.search.includes("userlogout=true") ||
    window.location.href.includes("adminlogout=true") ||
    window.location.href.includes("userlogout=true");

/** True for /user and /user/..., but not /users (client route). */
const isUserAreaPath = (pathname) =>
    pathname === "/user" || pathname.startsWith("/user/");

const Index = ({
    userData,
    loginSuccess,
    currentPosition,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const sessionBootstrapped = useRef(false);
    const tokenHandled = useRef(false);
    const sessionTokenInUrl = extractSessionTokenFromPath(location.pathname);
    const [tokenBootstrapComplete, setTokenBootstrapComplete] = useState(!sessionTokenInUrl);
    const logoutCallback = isLogoutCallback(location);
    const [logoutHandled, setLogoutHandled] = useState(!logoutCallback);

    const isFrontendRoute = FRONTEND_PATHS.includes(location.pathname);
    const hasStoredUser = userData && Object.keys(userData).length > 0;
    const normalizedUser = hasStoredUser ? normalizeLoginResponse(userData) : null;
    const role = normalizedUser?.role;
    const isAuthenticated = Boolean(role);
    const position = resolvePosition(role, currentPosition);

    useEffect(() => {
        const routeClass =
            location.pathname.startsWith("/admin") ||
            location.pathname.startsWith("/dashboard") ||
            location.pathname.startsWith("/instructor") ||
            isUserAreaPath(location.pathname)
                ? "admin-page"
                : "";
        document.body.className = i18n.language === "he" ? `hebrewClass ${routeClass}`.trim() : routeClass;
        return () => {
            document.body.className = i18n.language === "he" ? "hebrewClass" : "";
        };
    }, [location.pathname, i18n.language]);

    useEffect(() => {
        if (!logoutCallback || logoutHandled) {
            return;
        }

        const handlePostLogoutRedirect = async () => {
            await clearPersistedSession();
            const isAdmin =
                location.search.includes("adminlogout=true") ||
                window.location.href.includes("adminlogout=true");
            navigate(isAdmin ? "/admin/login" : "/login", { replace: true });
            setLogoutHandled(true);
        };

        handlePostLogoutRedirect();
    }, [logoutCallback, logoutHandled, location.search, navigate]);

    useEffect(() => {
        if (logoutCallback && !logoutHandled) {
            return;
        }

        if (sessionBootstrapped.current) {
            return;
        }
        sessionBootstrapped.current = true;

        if (!hasStoredUser) {
            return;
        }

        if (!normalizedUser?.role) {
            loginSuccess({});
            return;
        }

        if (!userData.role || !userData.token) {
            loginSuccess(normalizedUser);
        }

        if (!currentPosition && normalizedUser.role !== "admin") {
            dispatch(setCurrentPosition(normalizedUser.role));
        }
    }, [hasStoredUser, normalizedUser, userData, currentPosition, loginSuccess, dispatch]);

    useEffect(() => {
        if (tokenHandled.current) {
            return;
        }

        const token = extractSessionTokenFromPath(location.pathname);
        if (!token) {
            setTokenBootstrapComplete(true);
            return;
        }

        tokenHandled.current = true;

        try {
            const normalized = parseSessionToken(token);

            if (!normalized?.role) {
                setTokenBootstrapComplete(true);
                navigate("/login");
                return;
            }

            loginSuccess(normalized);

            if (normalized.role !== "admin") {
                dispatch(setCurrentPosition(normalized.role));
            }

            navigate(getDashboardPathForRole(normalized.role), { replace: true });
            setTokenBootstrapComplete(true);
        } catch (error) {
            console.error("Base64 decode error:", error);
            setTokenBootstrapComplete(true);
            navigate("/login");
        }
    }, [location.pathname, loginSuccess, navigate, dispatch]);

    useEffect(() => {
        if (logoutCallback && !logoutHandled) {
            return;
        }

        if (sessionTokenInUrl && !tokenBootstrapComplete) {
            return;
        }

        if (!isAuthenticated || !role) {
            return;
        }

        if (role === "admin" && !location.pathname.startsWith("/admin")) {
            navigate("/admin/dashboard", { replace: true });
            return;
        }

        if (role === "client" && (location.pathname === "/" || location.pathname === "/login")) {
            navigate("/dashboard", { replace: true });
            return;
        }

        if (role === "instructor" && !location.pathname.startsWith("/instructor")) {
            navigate("/instructor/dashboard", { replace: true });
            return;
        }

        if (role === "user" && !isUserAreaPath(location.pathname)) {
            navigate("/user/dashboard", { replace: true });
        }
    }, [isAuthenticated, role, location.pathname, navigate, sessionTokenInUrl, tokenBootstrapComplete, logoutCallback, logoutHandled]);

    if (logoutCallback && !logoutHandled) {
        return <div style={{ padding: 24 }}>Loading...</div>;
    }

    if (sessionTokenInUrl && !tokenBootstrapComplete) {
        return <div style={{ padding: 24 }}>Loading...</div>;
    }

    if (PUBLIC_AUTH_PATHS.includes(location.pathname)) {
        return <FrontendRoutes />;
    }

    if (!isAuthenticated) {
        if (location.pathname.startsWith("/admin")) {
            return <Navigate to="/admin/login" replace />;
        }
        return isFrontendRoute ? <FrontendRoutes /> : <Navigate to="/login" replace />;
    }

    if (role === "admin" && !location.pathname.startsWith("/admin")) {
        return <Navigate to="/admin/dashboard" replace />;
    }

    if (role === "client" && (location.pathname === "/" || location.pathname === "/login")) {
        return <Navigate to="/dashboard" replace />;
    }

    if (role === "instructor" && !location.pathname.startsWith("/instructor")) {
        return <Navigate to="/instructor/dashboard" replace />;
    }

    if (role === "user" && !isUserAreaPath(location.pathname)) {
        return <Navigate to="/user/dashboard" replace />;
    }

    if (role === "admin") {
        return (
            <>
                <RoleSwitchHandler />
                <AdminRoutes />
            </>
        );
    }

    if (position === "client") {
        return (
            <>
                <RoleSwitchHandler />
                <ClientRoutes />
            </>
        );
    }

    if (position === "instructor") {
        return (
            <>
                <RoleSwitchHandler />
                <InstructorRoutes />
            </>
        );
    }

    if (position === "user") {
        return (
            <>
                <RoleSwitchHandler />
                <UserRoutes />
            </>
        );
    }

    if (isFrontendRoute) {
        return <FrontendRoutes />;
    }

    return <Navigate to="/login" replace />;
};

const mapStateToProps = (globalState) => ({
    userData: globalState.mainReducerData.userData,
    accessibleUsers: globalState.mainReducerData.accessibleUsers,
    currentPosition: globalState.mainReducerData.currentPosition,
});

export default connect(mapStateToProps, { loginSuccess, setCurrentPosition })(Index);
